import { useCallback, useEffect, useState } from 'react';
import { Outlet, useMatch, useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useCurrentUser } from 'FrontRoyalAngular';
import { appHeaderActions } from 'AppHeaderReact';
import { useAppHeaderViewModel } from 'NavigationHelpers';
import { getUserProgramState } from 'Users';
import { MessagingProvider } from 'Messaging/components/MessagingProvider';
import { SyncUnreadToRedux } from 'Messaging/components/SyncUnreadToRedux';
import mobileBannerSrc from 'images/network-mobile-map-banner.png';
import { DesktopSectionNav, MobileSectionNav } from './components/FloatingSectionNav';
import { ScrollFadeContainer } from './components/ScrollFadeContainer';
import { MapBackground } from './components/MapBackground';
import { MapOverlays } from './components/map/MapOverlays';
import { ClusterPeoplePanel } from './components/map/ClusterPeoplePanel';
import { PartialAccessModal } from './components/PartialAccessModal';
import { OnboardingWizardModal } from './components/OnboardingWizardModal';
import { type ClusterData } from './api/studentNetworkV2Api.types';

const MOBILE_BREAKPOINT = 768;

// oxlint-disable-next-line max-lines-per-function, complexity -- layout shell with mobile/desktop variants and banner/gradient overlays
export function StudentNetworkV2() {
    const isMapView = !!useMatch('/student-network/map');
    const [selectedCluster, setSelectedCluster] = useState<ClusterData | null>(null);
    const [mobileNavHidden, setMobileNavHidden] = useState(false);
    const [mobileFadeOffset, setMobileFadeOffset] = useState(0);
    const currentUser = useCurrentUser();
    const upsId = getUserProgramState(currentUser)?.id;
    const partialDismissKey = `network-partial-dismissed-${upsId ?? 'none'}`;
    const [partialAccessDismissed, setPartialAccessDismissed] = useState(
        () => sessionStorage.getItem(partialDismissKey) === '1',
    );
    const dismissPartialAccess = useCallback(() => {
        setPartialAccessDismissed(true);
        sessionStorage.setItem(partialDismissKey, '1');
    }, [partialDismissKey]);
    const [wizardDismissed, setWizardDismissed] = useState(false);
    const showClusterPanel = isMapView && !!selectedCluster;
    const dispatch = useDispatch();

    // Set body background + brand class (e.g. body.valar) so brand-aware CSS works
    const appHeaderViewModel = useAppHeaderViewModel();
    useEffect(() => {
        appHeaderViewModel.setBodyBackground();
    }, [appHeaderViewModel]);

    // Hide the app header and add body class on mobile while the student network is mounted
    useEffect(() => {
        const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;

        const htmlEl = document.documentElement;

        document.body.classList.add('student-network');
        htmlEl.classList.add('student-network');
        if (isMobile()) {
            dispatch(appHeaderActions.setHidden(true));
            htmlEl.classList.add('student-network-no-html-pt');
        }

        const onResize = () => {
            const mobile = isMobile();
            dispatch(appHeaderActions.setHidden(mobile));
            htmlEl.classList.toggle('student-network-no-html-pt', mobile);
        };
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
            document.body.classList.remove('student-network');
            htmlEl.classList.remove('student-network', 'student-network-no-html-pt');
            dispatch(appHeaderActions.setHidden(false));
        };
    }, [dispatch]);

    // Determine which onboarding modal to show (mutually exclusive)
    const showOnboardingWizard =
        !wizardDismissed && currentUser?.hasAccessToFullStudentNetworkProfiles && !currentUser.hasSeenStudentNetwork;
    const showPartialAccess =
        !partialAccessDismissed && currentUser && !currentUser.hasAccessToFullStudentNetworkProfiles;

    return (
        <div className="student-network-v2 select-text-important relative h-full w-full overflow-hidden max-[767px]:pb-[55px] max-[767px]:notch:pb-[calc(55px+env(safe-area-inset-bottom))]">
            <MessagingProvider>
                <SyncUnreadToRedux />
            </MessagingProvider>
            <MapBackground
                blur={!!showOnboardingWizard}
                mapOverlays={
                    <MapOverlays
                        onClusterClick={setSelectedCluster}
                        visible={isMapView}
                        selectedClusterLatLng={
                            selectedCluster ? { lat: selectedCluster.lat, lng: selectedCluster.lng } : null
                        }
                    />
                }
                panTo={
                    showClusterPanel && selectedCluster ? { lat: selectedCluster.lat, lng: selectedCluster.lng } : null
                }
            />

            {/* Desktop: absolute layers for nav + cluster panel floating over the map */}
            <div className="pointer-events-none absolute inset-0 z-30 hidden md:flex md:flex-row md:gap-7.5 md:px-20 md:pt-[50px]">
                <div className="max-h-full shrink-0 overflow-hidden [&>*]:pointer-events-auto">
                    <DesktopSectionNav />
                </div>
                {showClusterPanel && (
                    <div className="pointer-events-auto max-h-full shrink-0">
                        <ClusterPeoplePanel cluster={selectedCluster} onClose={() => setSelectedCluster(null)} />
                    </div>
                )}
            </div>

            <div
                className={`absolute inset-0 hidden md:flex md:flex-row md:gap-7.5 md:px-20 md:pt-[50px] ${
                    isMapView ? 'pointer-events-none z-0' : 'z-10'
                }`}
            >
                <div className="hidden w-[220px] shrink-0 md:block" />
                <div className="relative min-h-0 flex-1">
                    <ScrollFadeContainer className="absolute inset-0 overflow-auto scrollbar-hide">
                        <Outlet context={{ setMobileNavHidden, setMobileFadeOffset, variant: 'desktop' as const }} />
                    </ScrollFadeContainer>
                </div>
            </div>

            {/* Mobile: flex column — nav stays at top, content scrolls below */}
            <div className={`absolute inset-0 z-30 flex flex-col md:hidden ${isMapView ? 'pointer-events-none' : ''}`}>
                {/* Banner image — non-map views only. Height = safe-area-inset-top (notch) + 28px visible banner */}
                {!isMapView && (
                    <div className="pointer-events-none absolute inset-x-0 top-0 z-[5] h-[calc(env(safe-area-inset-top,0px)+28px)] bg-white">
                        <img src={mobileBannerSrc} alt="" className="h-full w-full object-cover object-top" />
                    </div>
                )}

                {/* Gradient overlay — non-map views */}
                {!isMapView && (
                    <div
                        className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[calc(env(safe-area-inset-top,0px)+28px)]"
                        style={{
                            background: 'linear-gradient(to bottom, transparent 30%, white 85%)',
                        }}
                    />
                )}

                {/* Nav — hidden on map cluster panel and when child view requests it (e.g. profile detail) */}
                {!showClusterPanel && !mobileNavHidden && (
                    <div
                        className={`pointer-events-auto relative z-20 shrink-0 ${
                            isMapView
                                ? 'pt-[calc(env(safe-area-inset-top,0px)+15px)]'
                                : 'pt-[calc(env(safe-area-inset-top,0px)+25px)]'
                        }`}
                    >
                        <MobileSectionNav isMapView={isMapView} />
                    </div>
                )}

                {/* Scrollable content — pass-through on map view so map is interactive */}
                {/* White bg is a separate layer behind the masked scroll container
                    so the fade reveals white instead of the map on non-map views */}
                <div
                    className={`relative min-h-0 flex-1 max-[767px]:pb-[55px] max-[767px]:notch:pb-[calc(55px+env(safe-area-inset-bottom))] ${
                        isMapView ? '' : 'pointer-events-auto'
                    }`}
                >
                    {!isMapView && <div className="absolute inset-0 bg-white" />}
                    <ScrollFadeContainer
                        className={`relative h-full overflow-y-auto ${
                            !isMapView && mobileNavHidden ? 'pt-[calc(env(safe-area-inset-top,0px)+28px)]' : ''
                        }`}
                        fadeOffset={!isMapView && mobileNavHidden ? 28 : mobileFadeOffset}
                    >
                        <Outlet context={{ setMobileNavHidden, setMobileFadeOffset, variant: 'mobile' as const }} />
                    </ScrollFadeContainer>
                </div>
            </div>

            {/* Mobile cluster overlay — uses same banner+gradient layout */}
            {showClusterPanel && (
                <div className="absolute inset-0 z-30 flex flex-col max-[767px]:pb-[55px] max-[767px]:notch:pb-[calc(55px+env(safe-area-inset-bottom))] md:hidden">
                    <div className="pointer-events-none absolute inset-x-0 top-0 z-[5] h-[calc(env(safe-area-inset-top,0px)+28px)] bg-white">
                        <img src={mobileBannerSrc} alt="" className="h-full w-full object-cover object-top" />
                    </div>
                    <div
                        className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[calc(env(safe-area-inset-top,0px)+28px)]"
                        style={{ background: 'linear-gradient(to bottom, transparent 30%, white 85%)' }}
                    />
                    <div className="shrink-0 pt-[calc(env(safe-area-inset-top,0px)+28px)]" />
                    <div className="min-h-0 flex-1 overflow-y-auto bg-white">
                        <ClusterPeoplePanel cluster={selectedCluster} onClose={() => setSelectedCluster(null)} mobile />
                    </div>
                </div>
            )}

            {/* Onboarding modals */}
            {showPartialAccess && <PartialAccessModal onClose={dismissPartialAccess} />}
            {showOnboardingWizard && <OnboardingWizardModal onClose={() => setWizardDismissed(true)} />}
        </div>
    );
}

type NetworkOutletContext = {
    setMobileNavHidden: (hidden: boolean) => void;
    setMobileFadeOffset: (offset: number) => void;
    variant: 'desktop' | 'mobile';
};

export function useNetworkOutletContext() {
    return useOutletContext<NetworkOutletContext>();
}
